import { initClient } from '@ts-rest/core';
import { contract, GameOptions, PlayerTableViewModelV2 } from 'shared';
import { clearDevToken, DEV_MULTI_SESSION, getDevToken, setDevToken } from './devSession';

// Typed client generated from the shared contract. baseUrl "" keeps requests
// relative so the CRA dev proxy (and same-origin prod serving) forward the
// AuthToken cookie. Exposed directly for new code; the V2apiClient facade below
// preserves the call shapes existing components already use.
export const apiClient = initClient(contract, {
  baseUrl: '',
  credentials: 'include',
  // In dev, attach this tab's sessionStorage token so several tabs in one
  // browser can each be a different player (see devSession.ts). Evaluated per
  // request; returns "" (harmless — the server falls back to the cookie) when no
  // per-tab token is set or in production.
  baseHeaders: {
    Authorization: () => {
      const token = getDevToken();
      return token ? `Bearer ${token}` : '';
    },
  },
});

// A standardized API error body: { error: string }. Replaces the old
// { errorMessage } ApiError shape.
export interface ApiError {
  error: string;
}

export const V2apiClient = {
  getMe: async () => {
    // In dev multi-session, a tab counts as logged in only if it holds its own
    // per-tab token. Ignoring the shared cookie makes each new tab land on the
    // register screen so it can become a distinct player.
    if (DEV_MULTI_SESSION && !getDevToken()) return null;
    const res = await apiClient.getMe();
    return res.status === 200 ? res.body : null;
  },
  getOpenGames: async () => {
    const res = await apiClient.getOpenGames();
    return res.status === 200 ? res.body : [];
  },
  startNewGame: async (config: {
    title?: string;
    scoreToWin: number;
    isPrivate: boolean;
    numberOfPlayers: number;
    options: GameOptions;
  }) =>
    apiClient.openTable({
      body: {
        // Omitted when blank so the server names the table after the host.
        title: config.title?.trim() || undefined,
        scoreToWin: config.scoreToWin as any,
        isPrivate: config.isPrivate,
        numberOfPlayers: config.numberOfPlayers as any,
        options: config.options,
      },
    }),
  joinGame: async (tableId: string) => apiClient.joinTable({ params: { id: tableId } }),
  joinGameByCode: async (code: string) => apiClient.joinTableByCode({ params: { code } }),
  getGameState: async (): Promise<PlayerTableViewModelV2 | ApiError> => {
    const res = await apiClient.getGame();
    return res.body;
  },
  startGame: async (computers: boolean, locked: boolean = false) =>
    apiClient.startGame({ body: { computers, locked } }),
  changeSeat: async (seatIndex: number) => apiClient.changeSeat({ body: { seatIndex } }),
  sitDown: async (seatIndex: number) => apiClient.sitDown({ body: { seatIndex } }),
  makeBid: async (bid: number) => apiClient.makeBid({ body: { bid } }),
  playCard: async (cardId: string) => apiClient.playCard({ body: { cardId } }),
  playerReady: async () => apiClient.playerReady(),
  leaveGame: async () => apiClient.leaveTable(),
  fillSeatWithComputer: async (seatIndex: number) =>
    apiClient.fillSeatWithComputer({ body: { seatIndex } }),
  setTableLock: async (locked: boolean) => apiClient.setTableLock({ body: { locked } }),
  spectate: async () => apiClient.spectate(),
  logout: async () => {
    const res = await apiClient.logout();
    // The dev token lives in sessionStorage, outside the cookie the server just
    // cleared — drop it too or this tab keeps sending the revoked session.
    if (res.status === 200) clearDevToken();
    return res;
  },
  register: async (name: string) => {
    const res = await apiClient.register({ body: { name } });
    // Persist the dev token for this tab so subsequent requests/sockets
    // authenticate as this newly registered user (dev only; undefined in prod).
    if (res.status === 200 && res.body.token) setDevToken(res.body.token);
    return res;
  },
};

export function isErrorResponse(response: unknown): response is ApiError {
  return typeof response === 'object' && response !== null && 'error' in response;
}

/*
 * ts-rest resolves a non-2xx response as a value rather than throwing, so
 * callers have to look at `status` themselves. This returns the server's error
 * text for a failed response and undefined for a successful one, which is what
 * the toast wiring needs.
 */
export function apiErrorMessage(response: { status: number; body: unknown }): string | undefined {
  if (response.status >= 200 && response.status < 300) return undefined;
  return isErrorResponse(response.body) ? response.body.error : 'Something went wrong.';
}
